import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { Link } from 'react-router';

import Button from '@/componentsv2/Button';
import { ERROR_CODES, firstErrorCode } from '@/core/api';

import AccountEngagementsList from './AccountEngagementsList';
import StaffEngagementsList from './StaffEngagementsList';
import StatusBadge from './StatusBadge';
import { formatNteHours } from './money';
import { nextActionCopyFor, nextActionHref } from './nextActionCopy';
import { ACCOUNT_ENGAGEMENT_GATE, MY_ENGAGEMENT_BUCKETS } from './operations';
import { useIsInternalStaff, useSessionViewer } from './session';
import {
  type EngagementBucketsView,
  type EngagementListRow,
  fromEngagementBuckets,
} from './viewModel';

/**
 * `/engagements` — the viewer's own engagements, bucketed (§9.6).
 *
 * The flag gate is one level up in `EngagementsRoute`.
 *
 * **Both sides land here.** `myEngagementBuckets` became viewer-aware in
 * ENG-5609, so the page renders the expert's engagements to an expert and the
 * customer's to a customer, each bucketed by what that viewer owes. The row's
 * badge takes its role per-row rather than per-session, because one viewer can
 * be the expert on one engagement and the customer on the next.
 *
 * Rows render from the wire shape, not the prototype's `EngagementScene` —
 * see `viewModel.ts` for why that adapter cannot exist. `StatusBadge` and
 * `formatNteHours` are the live twins shared with the Hub and the project
 * section, so the three cannot drift in status or hours copy.
 */
/**
 * `/engagements` — the viewer's own engagements, plus two conditional tabs: the
 * customer power user's account-wide roll-up (ENG-5436 / FE6) and, for OF staff,
 * every engagement on the platform (ENG-5684).
 *
 * Tabs rather than routes, on purpose. `/engagements/:id` is the Hub, so a
 * static `/engagements/all` would rank against it, and ENG-5642 is the standing
 * lesson about what a sibling under this path does to the nav's active rule:
 * `isNavItemActive` matches on prefix, so the sibling lights up Engagements as
 * well as itself. One nav item, one route, split inside. No URL param either,
 * so no deep link — these are places you go, not links you send.
 *
 * The tabs do NOT change what `/engagements` means. "Mine" is
 * `myEngagementBuckets`, which stays personal for every viewer including staff
 * and account owners (ENG-5636's `list ⊆ openable` rule). The other two are
 * separate read models behind their own gates, and all three of Mine's buckets
 * are the viewer's own.
 *
 * **The two gates are different in kind.** Staff is a session fact
 * (`useIsInternalStaff`), known before any query. The power-user scope is a DATA
 * relation the api derives from the viewer's `team_members` rows and never
 * accepts as an argument, so the only honest way to ask "may this viewer see the
 * roll-up" is to ask the api. `ACCOUNT_ENGAGEMENT_GATE` is that question, and it
 * is a COUNT rather than a page: this runs for every viewer on every visit, and
 * most of them are asking a question whose answer is no. The roll-up fetches its
 * own filter options once opened. It fails closed: no tab on error, and no tab
 * until a fresh answer arrives, see its `fetchPolicy` for why a cached one will
 * not do.
 */
export default function EngagementsPage(): JSX.Element {
  const isInternalStaff = useIsInternalStaff();
  const [tab, setTab] = useState<'mine' | 'account' | 'all'>('mine');

  const gateQuery = useQuery(ACCOUNT_ENGAGEMENT_GATE, {
    // network-only, because this answer is a permission gate. Under
    // `cache-and-network` a cached success paints the tab for a session that has
    // since lost the scope: a role change, a "view as" switch, a sign-in as
    // someone else. Fail closed costs one aggregate per visit to `/engagements`
    // and cannot be stale-permissive.
    fetchPolicy: 'network-only',
    // The denial is the expected answer for most viewers, so it must not reach
    // the error link and surface as a toast or a Sentry event.
    errorPolicy: 'all',
  });
  const canSeeAccountRollup = Boolean(gateQuery.data?.accountEngagementStats);

  // A denial and a dropped connection are NOT the same answer, even though both
  // leave `canSeeAccountRollup` false. Hiding the tab is right for the denial,
  // which is the expected answer for most viewers. Doing it silently for a
  // network failure loses a power user their tab for the whole session with
  // nothing to click, so that case says so and offers a retry.
  const gateDenied =
    gateQuery.error !== undefined &&
    firstErrorCode(gateQuery.error) === ERROR_CODES.PERMISSION_NOT_ALLOWED;
  const gateFailed = gateQuery.error !== undefined && !gateDenied && !canSeeAccountRollup;

  // Defensive: if the session refreshes into a viewer who no longer holds a tab
  // while that tab is open, fall back rather than render a list that can only
  // deny.
  const available = {
    mine: true,
    account: canSeeAccountRollup,
    all: isInternalStaff,
  };
  const activeTab = available[tab] ? tab : 'mine';
  const showTabs = available.account || available.all;

  return (
    <Shell>
      {/* `flex-wrap`, matching every other tab/filter row in this feature: at
          375px three labels no longer fit the shell's width. */}
      {showTabs ? (
        <div
          className="flex flex-wrap items-center gap-8"
          role="tablist"
          aria-label="Engagements view"
        >
          <TabButton label="Mine" selected={activeTab === 'mine'} onClick={() => setTab('mine')} />
          {available.account ? (
            <TabButton
              label="My account"
              selected={activeTab === 'account'}
              onClick={() => setTab('account')}
            />
          ) : null}
          {available.all ? (
            <TabButton
              label="All engagements"
              selected={activeTab === 'all'}
              onClick={() => setTab('all')}
            />
          ) : null}
        </div>
      ) : null}

      {gateFailed ? (
        <p className="text-on-surface-muted body-2" role="status">
          We couldn&rsquo;t check your account&rsquo;s engagements.{' '}
          <button type="button" className="underline" onClick={() => void gateQuery.refetch()}>
            Try again
          </button>
        </p>
      ) : null}

      {activeTab === 'all' ? <StaffEngagementsList /> : null}
      {activeTab === 'account' ? <AccountEngagementsList /> : null}
      {activeTab === 'mine' ? <MyEngagements /> : null}
    </Shell>
  );
}

function TabButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={`min-h-[44px] border-b-2 px-12 py-8 transition-colors subtitle-2 ${
        selected
          ? 'border-brand-primary text-on-surface'
          : 'border-transparent text-on-surface-muted hover:text-on-surface'
      }`}
    >
      {label}
    </button>
  );
}

function MyEngagements(): JSX.Element {
  const { data, loading, error, refetch } = useQuery(MY_ENGAGEMENT_BUCKETS, {
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <div
        className="h-96 animate-pulse rounded-lg bg-surface-elevated"
        aria-label="Loading engagements"
      />
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-surface-border bg-surface-card p-24">
        <h2 className="text-on-surface hd-5">We couldn’t load your engagements.</h2>
        <p className="mt-8 text-on-surface-muted body-2">
          The connection may have dropped. Try again in a moment.
        </p>
        <div className="mt-16">
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  const buckets: EngagementBucketsView = fromEngagementBuckets(
    data?.myEngagementBuckets ?? { actionNeeded: [], active: [], closed: [] }
  );
  const total = buckets.actionNeeded.length + buckets.active.length + buckets.closed.length;

  return (
    <>
      {total === 0 ? (
        <div
          className="flex flex-col items-center gap-8 rounded-lg border border-dashed border-surface-border bg-surface-elevated px-24 py-48 text-center"
          role="region"
          aria-label="No engagements yet"
        >
          <h2 className="text-on-surface hd-6">No engagements yet</h2>
          <p className="max-w-[420px] text-on-surface-muted body-2">
            When a customer formalizes an engagement with you, it’ll appear here.
          </p>
        </div>
      ) : (
        <>
          <Bucket label="Action needed" caption="Waiting on you." rows={buckets.actionNeeded} />
          <Bucket label="Active" rows={buckets.active} />
          <Bucket label="Closed" rows={buckets.closed} />
        </>
      )}
    </>
  );
}

/**
 * One frame for all three tabs (ENG-5736).
 *
 * The width used to be per-tab — 920px on Mine, 1200px everywhere else — and
 * because `Body` caps the page at 1170px and centres it, that swap moved the H1
 * 110px sideways on every tab click. The frame is the page's, not the panel's,
 * so it is a constant: only the panel below swaps.
 *
 * 1200px is the prototype's engagements-list container (§S6 Expert Dashboard
 * Engagements), whose class list this otherwise matches character for
 * character. `Body` clamps it to 1140px in practice; the number stays 1200 so
 * the two shells read as the same shell.
 *
 * `min-h-screen` is the other half of the fix. Mine's empty state is short and
 * the two tables are tall, so on a roomy viewport the window scrollbar appeared
 * and disappeared across the switch — a second horizontal shift, and a vertical
 * one where the scroll offset got clamped. Holding the frame at a viewport
 * height keeps the page overflowing on all three, so the scrollbar never flips.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    // The test id is how the frame test reaches this div. It carries no role
    // and no accessible name, which is the case `getByTestId` is the last
    // resort *for*; the alternative, walking up from the H1, silently retargets
    // the moment anything wraps the header. `Body` sets one for the same reason.
    <div
      data-testid="engagements-frame"
      className="mx-auto flex min-h-screen w-full max-w-[1200px] flex-col gap-24 px-16 py-24 sm:px-24"
    >
      <header className="flex flex-col gap-4 border-b border-surface-border pb-16">
        <h1 className="text-on-surface hd-3">Engagements</h1>
      </header>
      {children}
    </div>
  );
}

interface BucketProps {
  label: string;
  caption?: string;
  rows: EngagementListRow[];
}

function Bucket({ label, caption, rows }: BucketProps): JSX.Element | null {
  // Empty buckets are hidden; the all-empty case is handled by the caller.
  if (rows.length === 0) return null;
  return (
    <section className="flex flex-col gap-12">
      <div className="flex items-baseline gap-12">
        <h2 className="text-on-surface hd-5">{label}</h2>
        <span className="text-on-surface-muted subtitle-2">{rows.length}</span>
      </div>
      {caption ? <p className="-mt-8 text-on-surface-muted body-2">{caption}</p> : null}
      <ul className="flex list-none flex-col gap-12 p-[0]">
        {rows.map((row) => (
          <li key={row.engagementId}>
            <Row row={row} />
          </li>
        ))}
      </ul>
    </section>
  );
}

interface RowProps {
  row: EngagementListRow;
}

function Row({ row }: RowProps): JSX.Element {
  const { userId: viewerUserId } = useSessionViewer();
  const nextAction = nextActionCopyFor(row.nextAction);
  const viewerIsExpert = Boolean(row.expertId) && row.expertId === viewerUserId;
  /*
    Who the engagement is with, on the meta line beside the hours — the same
    composition the project page card uses (ENG-5730). The project name is the
    title and does not identify the engagement on its own: one project can
    carry several, each with a different expert.

    Omitted when the viewer IS the row's expert. This list is mixed since
    ENG-5609, and naming the expert to the expert says nothing; their
    counterparty is the customer, whom no list field names.
  */
  const expert = viewerIsExpert
    ? null
    : (row.expertName ?? (row.expertId === null ? 'Awaiting expert' : 'Expert'));
  const hours = formatNteHours(row.nteHoursConsumed, row.nteHoursTotal);
  return (
    <Link
      to={nextActionHref(row.engagementId, row.nextAction)}
      className="flex flex-col gap-8 rounded-lg border border-surface-border bg-surface-card p-16 no-underline hover:border-brand-primary sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-4">
        <span className="text-on-surface subtitle-1">
          {row.projectName ?? 'Untitled engagement'}
        </span>
        <span className="text-on-surface-muted body-2">
          {expert === null ? hours : `${expert} · ${hours}`}
        </span>
        {/* Emphasis, not link styling: the whole row is the one link, and in
            this feature `text-brand-primary` is the clickable affordance
            (every other use of it sits on an anchor or a button). Weight and
            full-contrast ink carry the priority without a second target. */}
        {nextAction ? (
          <span className="font-medium text-on-surface body-2">{nextAction}</span>
        ) : null}
      </div>
      {/* Per-row, not per-session: `statusBadgeTextFor` only ever asks whether
          the viewer is the expert, so expert-or-not is the exact granularity it
          needs, and `expertId` answers it without selecting participants.
          The non-expert side is `unknown`, not `work_approver` — the list now
          reaches plain project members too, and a row cannot tell an approver
          from a member without the roster. Both read the same badge copy, so
          this is the honest label for the same output rather than a claim the
          data can't support. Guarded on truthiness like `roles.ts`: `userId` is
          `''` for a logged-out viewer, and `expertId` is a nullable `String`,
          so `'' === ''` would badge a customer as the expert. */}
      <StatusBadge
        status={row.status}
        awaiting={row.awaiting}
        viewerRole={viewerIsExpert ? 'expert' : 'unknown'}
      />
    </Link>
  );
}
